import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { 
  Shield, ArrowLeft, Search, Wrench, Trash2, ArrowRightLeft,
  MoreVertical, AlertTriangle, Package, ShieldAlert, ShoppingCart, Trash, Loader2,
  Pencil, Upload, Camera
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  Active: "status-active",
  Stolen: "status-stolen",
  Sold: "status-sold",
  Discarded: "status-discarded"
};

const STATUS_ICONS = {
  Active: Package,
  Stolen: ShieldAlert,
  Sold: ShoppingCart,
  Discarded: Trash
};

export default function AdminTools() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [tools, setTools] = useState([]);
  const [stolenTools, setStolenTools] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedTool, setSelectedTool] = useState(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newOwnerId, setNewOwnerId] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    manufacturer: "",
    tool_type: "",
    serial_number: "",
    model_number: "",
    description: "",
    estimated_value: "",
    status: ""
  });

  useEffect(() => {
    fetchTools();
    fetchStolenTools();
  }, [searchQuery, statusFilter]);

  const fetchTools = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter) params.append("status", statusFilter);

      const response = await fetch(`${API}/admin/tools?${params}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        throw new Error("Failed to fetch tools");
      }

      const data = await response.json();
      setTools(data.tools);
      setTotal(data.total);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStolenTools = async () => {
    try {
      const response = await fetch(`${API}/admin/tools/stolen`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStolenTools(data.tools);
      }
    } catch (error) {
      console.error("Failed to fetch stolen tools");
    }
  };

  const handleTransfer = async () => {
    if (!selectedTool || !newOwnerId) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API}/admin/tools/${selectedTool.tool_id}/transfer?new_owner_id=${newOwnerId}`,
        { method: "PUT", headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success("Tool transferred successfully");
        setShowTransferDialog(false);
        setNewOwnerId("");
        fetchTools();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Transfer failed");
      }
    } catch (error) {
      toast.error("Transfer failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedTool || !newStatus) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API}/admin/tools/${selectedTool.tool_id}/status?status=${newStatus}`,
        { method: "PUT", headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success(`Status changed to ${newStatus}`);
        setShowStatusDialog(false);
        setNewStatus("");
        fetchTools();
        fetchStolenTools();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Status change failed");
      }
    } catch (error) {
      toast.error("Status change failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTool) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/tools/${selectedTool.tool_id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success("Tool deleted");
        setShowDeleteDialog(false);
        fetchTools();
        fetchStolenTools();
      }
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = (tool) => {
    setSelectedTool(tool);
    setEditForm({
      manufacturer: tool.manufacturer || "",
      tool_type: tool.tool_type || "",
      serial_number: tool.serial_number || "",
      model_number: tool.model_number || "",
      description: tool.description || "",
      estimated_value: tool.estimated_value || "",
      status: tool.status || "Active"
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedTool) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/admin/tools/${selectedTool.tool_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          manufacturer: editForm.manufacturer,
          tool_type: editForm.tool_type,
          serial_number: editForm.serial_number,
          model_number: editForm.model_number || null,
          description: editForm.description || null,
          estimated_value: editForm.estimated_value ? parseFloat(editForm.estimated_value) : null,
          status: editForm.status
        })
      });
      if (response.ok) {
        toast.success("Tool updated successfully");
        setShowEditDialog(false);
        fetchTools();
        fetchStolenTools();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Update failed");
      }
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTool) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await fetch(`${API}/admin/tools/${selectedTool.tool_id}/upload-image`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Image uploaded successfully");
        // Update the selected tool with new image
        setSelectedTool(prev => ({ ...prev, image_url: data.image_url }));
        fetchTools();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 px-4 py-3 lg:px-8">
          <Link to="/admin">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Admin
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <span className="font-heading text-lg font-bold uppercase">Tool Oversight</span>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-7xl mx-auto">
        {/* Stolen Tools Alert */}
        {stolenTools.length > 0 && (
          <Card className="border-2 border-red-500/50 bg-red-500/5 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading uppercase text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Stolen Tools Report ({stolenTools.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stolenTools.slice(0, 6).map((tool) => (
                  <div 
                    key={tool.tool_id} 
                    className="p-3 bg-red-500/10 rounded border border-red-500/30"
                  >
                    <p className="font-medium">{tool.manufacturer} {tool.tool_type}</p>
                    <p className="text-xs text-muted-foreground font-mono">S/N: {tool.serial_number}</p>
                    <p className="text-xs text-muted-foreground">Owner: {tool.owner_name}</p>
                    <p className="text-xs text-muted-foreground">{tool.owner_email}</p>
                  </div>
                ))}
              </div>
              {stolenTools.length > 6 && (
                <p className="text-sm text-muted-foreground mt-3">
                  And {stolenTools.length - 6} more stolen tools...
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by serial number, tool ID, manufacturer, or owner name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-card border-input"
              data-testid="tool-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px] bg-card" data-testid="tool-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Stolen">Stolen</SelectItem>
              <SelectItem value="Sold">Sold</SelectItem>
              <SelectItem value="Discarded">Discarded</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tools Count */}
        <p className="text-muted-foreground text-sm mb-4">
          Showing {tools.length} of {total} tools
        </p>

        {/* Tools List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading tools...</p>
          </div>
        ) : tools.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No tools found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tools.map((tool) => {
              const StatusIcon = STATUS_ICONS[tool.status] || Package;
              const API_BASE = process.env.REACT_APP_BACKEND_URL;
              const imageUrl = tool.image_url 
                ? (tool.image_url.startsWith('http') ? tool.image_url : API_BASE + tool.image_url)
                : null;
              
              return (
                <Card 
                  key={tool.tool_id} 
                  className="border-border bg-card cursor-pointer hover:border-primary/50 transition-colors" 
                  data-testid={`admin-tool-card-${tool.tool_id}`}
                  onClick={() => navigate(`/tool/${tool.tool_id}`, { state: { from: 'admin' } })}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <Badge className={`${STATUS_STYLES[tool.status]} border font-mono text-xs uppercase`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {tool.status}
                      </Badge>
                      <div className="flex items-center gap-2">
                        {/* Tool Image Thumbnail */}
                        {imageUrl && (
                          <div className="w-10 h-10 rounded overflow-hidden bg-black/20 flex-shrink-0">
                            <img 
                              src={imageUrl} 
                              alt={tool.manufacturer}
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(tool);
                            }}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit Tool
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTool(tool);
                              setShowStatusDialog(true);
                            }}>
                              <Package className="w-4 h-4 mr-2" /> Change Status
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTool(tool);
                              setShowTransferDialog(true);
                            }}>
                              <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer Ownership
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTool(tool);
                                setShowDeleteDialog(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete Tool
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <h3 className="font-heading text-lg font-bold uppercase mb-1">
                      {tool.manufacturer}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-2">{tool.tool_type}</p>

                    <div className="space-y-1 text-sm">
                      <p className="font-mono text-xs">ID: {tool.tool_id}</p>
                      <p className="text-muted-foreground">
                        <span className="text-foreground/70">S/N:</span>{" "}
                        <span className="font-mono">{tool.serial_number}</span>
                      </p>
                      {tool.model_number && (
                        <p className="text-muted-foreground">
                          <span className="text-foreground/70">Model:</span>{" "}
                          <span className="font-mono">{tool.model_number}</span>
                        </p>
                      )}
                    </div>

                    <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                      <p>Owner: {tool.owner_name}</p>
                      <p>{tool.owner_email}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Transfer Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Transfer Tool Ownership</DialogTitle>
              <DialogDescription>
                Transfer <strong>{selectedTool?.manufacturer} {selectedTool?.tool_type}</strong> to another user.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm text-muted-foreground mb-2 block">New Owner User ID</label>
              <Input
                placeholder="e.g., user_abc123def456"
                value={newOwnerId}
                onChange={(e) => setNewOwnerId(e.target.value)}
                className="bg-background"
                data-testid="transfer-owner-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Find the user ID in the User Management section
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleTransfer}
                disabled={actionLoading || !newOwnerId}
                className="btn-primary"
                data-testid="confirm-transfer-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Transfer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Change Tool Status</DialogTitle>
              <DialogDescription>
                Update status for <strong>{selectedTool?.manufacturer} {selectedTool?.tool_type}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm text-muted-foreground mb-2 block">New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-background" data-testid="new-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Stolen">Stolen</SelectItem>
                  <SelectItem value="Sold">Sold</SelectItem>
                  <SelectItem value="Discarded">Discarded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleStatusChange}
                disabled={actionLoading || !newStatus}
                className="btn-primary"
                data-testid="confirm-status-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-destructive">Delete Tool</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this tool? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={actionLoading}
                data-testid="confirm-delete-tool-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete Tool
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Tool Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Edit Tool</DialogTitle>
              <DialogDescription>
                Update details for tool ID: <span className="font-mono">{selectedTool?.tool_id}</span>
              </DialogDescription>
            </DialogHeader>
            
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            
            <div className="space-y-4 py-4">
              {/* Tool Image Section */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Tool Photo</Label>
                <div className="flex items-center gap-4">
                  {selectedTool?.image_url ? (
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-black/20">
                      <img 
                        src={selectedTool.image_url.startsWith('http') ? selectedTool.image_url : process.env.REACT_APP_BACKEND_URL + selectedTool.image_url}
                        alt="Tool"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-muted flex items-center justify-center">
                      <Camera className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {selectedTool?.image_url ? "Replace Photo" : "Upload Photo"}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="manufacturer">Manufacturer *</Label>
                  <Input
                    id="manufacturer"
                    value={editForm.manufacturer}
                    onChange={(e) => setEditForm(prev => ({ ...prev, manufacturer: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tool_type">Tool Type *</Label>
                  <Input
                    id="tool_type"
                    value={editForm.tool_type}
                    onChange={(e) => setEditForm(prev => ({ ...prev, tool_type: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serial_number">Serial Number *</Label>
                  <Input
                    id="serial_number"
                    value={editForm.serial_number}
                    onChange={(e) => setEditForm(prev => ({ ...prev, serial_number: e.target.value }))}
                    className="bg-background font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model_number">Model Number</Label>
                  <Input
                    id="model_number"
                    value={editForm.model_number}
                    onChange={(e) => setEditForm(prev => ({ ...prev, model_number: e.target.value }))}
                    className="bg-background font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estimated_value">Estimated Value (£)</Label>
                  <Input
                    id="estimated_value"
                    type="number"
                    step="0.01"
                    value={editForm.estimated_value}
                    onChange={(e) => setEditForm(prev => ({ ...prev, estimated_value: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_status">Status</Label>
                  <Select value={editForm.status} onValueChange={(val) => setEditForm(prev => ({ ...prev, status: val }))}>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Stolen">Stolen</SelectItem>
                      <SelectItem value="Sold">Sold</SelectItem>
                      <SelectItem value="Discarded">Discarded</SelectItem>
                      <SelectItem value="Written Off">Written Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-background min-h-[80px]"
                  placeholder="Additional details about the tool..."
                />
              </div>

              {/* Owner Info (read-only) */}
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-1">Owner Information</p>
                <p className="text-sm">{selectedTool?.owner_name}</p>
                <p className="text-sm text-muted-foreground">{selectedTool?.owner_email}</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleEditSubmit}
                disabled={actionLoading || !editForm.manufacturer || !editForm.tool_type || !editForm.serial_number}
                className="btn-primary"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
